import React, { useMemo, useState } from 'react';
import {
  faChevronDown,
  faChevronRight,
  faCopy,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import MainButton from '../../../components/agv-button';
import { JSON_COLORS } from '../logs.utils';

function Primitive({ value }) {
  if (value === null) {
    return <span className={JSON_COLORS.literal}>null</span>;
  }
  if (typeof value === 'string') {
    return (
      <span className={`${JSON_COLORS.string} tw-break-all`}>
        &quot;{value}&quot;
      </span>
    );
  }
  if (typeof value === 'number') {
    return <span className={JSON_COLORS.number}>{value}</span>;
  }
  return <span className={JSON_COLORS.literal}>{String(value)}</span>;
}

// Dépliage initial des noeuds : deux premiers niveaux, tout déplié ou tout replié
const EXPAND_MODE = {
  DEFAULT: 'DEFAULT',
  ALL_OPEN: 'ALL_OPEN',
  ALL_CLOSED: 'ALL_CLOSED',
};

// Recherche insensible à la casse et aux accents
const normalize = (text) =>
  String(text).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Seuls les noms de clés d'objets sont recherchés (pas les positions dans les listes)
const isKeyMatch = (name, search) =>
  Boolean(search) &&
  typeof name === 'string' &&
  normalize(name).includes(search);

const getEntries = (value) =>
  Array.isArray(value)
    ? value.map((item, index) => [index, item])
    : Object.entries(value);

function hasMatchingKey(value, search) {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  return getEntries(value).some(
    ([key, item]) => isKeyMatch(key, search) || hasMatchingKey(item, search)
  );
}

function countMatchingKeys(value, search) {
  if (value === null || typeof value !== 'object') {
    return 0;
  }
  return getEntries(value).reduce(
    (count, [key, item]) =>
      count +
      (isKeyMatch(key, search) ? 1 : 0) +
      countMatchingKeys(item, search),
    0
  );
}

function HighlightedKey({ name, search }) {
  const text = String(name);
  if (!isKeyMatch(name, search)) {
    return text;
  }
  const start = normalize(text).indexOf(search);
  return (
    <>
      {text.slice(0, start)}
      <mark className={JSON_COLORS.currentMatch}>
        {text.slice(start, start + search.length)}
      </mark>
      {text.slice(start + search.length)}
    </>
  );
}

/**
 * Noeud de l'arbre. Avec une recherche active, seules les branches menant à une clé trouvée
 * sont affichées (dépliées) ; une clé trouvée affiche tout son contenu.
 */
function JsonNode({ name, value, depth, expandMode, search }) {
  const isContainer = value !== null && typeof value === 'object';
  const [isOpen, setIsOpen] = useState(() =>
    expandMode === EXPAND_MODE.DEFAULT
      ? depth < 2
      : expandMode === EXPAND_MODE.ALL_OPEN
  );

  const keyMatches = isKeyMatch(name, search);
  const isVisible = !search || keyMatches || hasMatchingKey(value, search);
  if (!isVisible) {
    return null;
  }

  const label =
    name !== undefined ? (
      <span className={JSON_COLORS.key}>
        <HighlightedKey name={name} search={search} />
        <span className={JSON_COLORS.punctuation}>: </span>
      </span>
    ) : null;

  if (!isContainer) {
    return (
      <div className={depth > 1 ? 'tw-pl-5' : ''}>
        {label}
        <Primitive value={value} />
      </div>
    );
  }

  const entries = getEntries(value);
  const [openBracket, closeBracket] = Array.isArray(value)
    ? ['[', ']']
    : ['{', '}'];
  // Sous une clé trouvée, tout le contenu s'affiche sans filtrage
  const children = entries.map(([key, item]) => (
    <JsonNode
      key={key}
      name={key}
      value={item}
      depth={depth + 1}
      expandMode={expandMode}
      search={keyMatches ? '' : search}
    />
  ));

  // La racine reste toujours dépliée : on replie ses blocs (DLA_TOURNE, DLA_CLITOU...)
  if (depth === 0) {
    return <div className="tw-flex tw-flex-col">{children}</div>;
  }

  // Une branche qui mène à un résultat de recherche est toujours dépliée
  const isExpanded = Boolean(search && !keyMatches) || isOpen;

  return (
    <div className={depth > 1 ? 'tw-pl-5' : ''}>
      <button
        type="button"
        onClick={() => setIsOpen(!isExpanded)}
        aria-expanded={isExpanded}
        className="tw-inline-flex tw-items-center tw-gap-1 tw-bg-transparent tw-border-0 tw-p-0 tw-text-left tw-font-mono tw-text-xs tw-cursor-pointer"
      >
        <FontAwesomeIcon
          className={`tw-size-2.5 ${JSON_COLORS.punctuation}`}
          icon={isExpanded ? faChevronDown : faChevronRight}
        />
        {label}
        <span className={JSON_COLORS.punctuation}>
          {openBracket}
          {!isExpanded &&
            ` ${entries.length} élément${entries.length > 1 ? 's' : ''} ${closeBracket}`}
        </span>
      </button>
      {isExpanded && (
        <>
          {children}
          <div className={`tw-pl-5 ${JSON_COLORS.punctuation}`}>
            {closeBracket}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Affichage des données envoyées en arbre repliable, avec recherche par nom de clé.
 * Rendu en texte uniquement (aucun HTML interprété).
 */
export default function LogPayloadViewer({ datas }) {
  // « Tout déplier » / « Tout replier » recréent l'arbre (nouvelle key) avec le dépliage demandé
  const [expand, setExpand] = useState({
    mode: EXPAND_MODE.DEFAULT,
    version: 0,
  });
  const [searchText, setSearchText] = useState('');
  const search = normalize(searchText.trim());
  const matchCount = useMemo(
    () => (search ? countMatchingKeys(datas, search) : 0),
    [datas, search]
  );

  const expandAll = (mode) =>
    setExpand(({ version }) => ({ mode, version: version + 1 }));

  const copyPayload = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(datas, null, 2));
      toast.success('Données copiées dans le presse-papiers.');
    } catch {
      toast.error('Impossible de copier les données.');
    }
  };

  if (datas === null || datas === undefined) {
    return (
      <p className="tw-text-sm tw-text-text-dark-medium-importance">
        Aucune donnée envoyée.
      </p>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-2 tw-min-h-0">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        <input
          type="search"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder="Rechercher une clé (ex. KMS)"
          aria-label="Rechercher une clé dans les données envoyées"
          className="tw-flex-1 tw-min-w-[180px] tw-px-3 tw-py-1.5 tw-text-sm tw-rounded tw-border tw-border-solid tw-border-black/20 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-main"
        />
        <MainButton
          variant="secondary"
          text="Tout déplier"
          onClick={() => expandAll(EXPAND_MODE.ALL_OPEN)}
        />
        <MainButton
          variant="secondary"
          text="Tout replier"
          onClick={() => expandAll(EXPAND_MODE.ALL_CLOSED)}
        />
        <MainButton
          variant="secondary"
          icon={faCopy}
          text="Copier"
          onClick={copyPayload}
        />
      </div>
      {search && (
        <p
          className="tw-m-0 tw-text-xs tw-text-text-dark-medium-importance"
          role="status"
        >
          {matchCount
            ? `${matchCount} clé${matchCount > 1 ? 's' : ''} trouvée${matchCount > 1 ? 's' : ''}`
            : 'Aucune clé ne correspond à la recherche.'}
        </p>
      )}
      <div
        className={`tw-overflow-auto tw-p-3 tw-rounded-md ${JSON_COLORS.background} ${JSON_COLORS.text} tw-font-mono tw-text-xs tw-leading-5`}
      >
        <JsonNode
          key={expand.version}
          value={datas}
          depth={0}
          expandMode={expand.mode}
          search={search}
        />
      </div>
    </div>
  );
}
